import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Loader } from 'lucide-react';

const DonationModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    amount: '',
    customAmount: '',
    paymentMethod: 'card',
    isMonthly: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const predefinedAmounts = [10, 25, 50, 100];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAmountSelect = (amount) => {
    setFormData(prev => ({ ...prev, amount: amount.toString(), customAmount: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Navigate to donate page instead of external redirect
    navigate('/get-involved/donate');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal show d-block"
      style={{ background: 'rgba(0,0,0,0.6)', zIndex: 9999 }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-sm modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header text-white" style={{ background: 'linear-gradient(to right, #6B8E23, #556B1F)' }}>
            <div className="d-flex flex-column align-items-center w-100">
              <button
                onClick={onClose}
                type="button"
                className="btn-close btn-close-white position-absolute"
                style={{ top: '0.5rem', right: '0.5rem' }}
                aria-label="Close"
              />
              <div className="mb-2">
                <Heart className="text-white" size={24} style={{ fill: 'currentColor' }} />
              </div>
              <h5 className="modal-title text-center mb-0">Support ARCA</h5>
              <small className="text-light">Make a Difference Today</small>
            </div>
          </div>

          {/* Body */}
          <div className="modal-body p-4">
            {success ? (
              <div className="text-center py-4">
                <Heart className="text-danger mb-3" size={48} style={{ fill: 'currentColor' }} />
                <h5 className="text-success mb-2">Thank You!</h5>
                <p className="text-muted">Your donation has been received.</p>
              </div>
            ) : (
              <>
                <p className="text-muted text-center mb-3 small">
                  Help us BREATHE LIFE into Africa by supporting our initiatives
                </p>

                {error && (
                  <div className="alert alert-danger alert-sm py-2 px-3 mb-3" role="alert">
                    <small>{error}</small>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  {/* Amount Selection */}
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Amount (USD)</label>
                    <div className="row g-2 mb-2">
                      {predefinedAmounts.map(amount => (
                        <div key={amount} className="col-3">
                          <button
                            type="button"
                            onClick={() => handleAmountSelect(amount)}
                            className={`btn w-100 btn-sm fw-bold ${formData.amount === amount.toString()
                                ? 'btn-primary'
                                : 'btn-outline-primary'
                              }`}
                          >
                            ${amount}
                          </button>
                        </div>
                      ))}
                    </div>
                    <input
                      type="number"
                      name="customAmount"
                      value={formData.customAmount}
                      onChange={(e) => setFormData(prev => ({ ...prev, customAmount: e.target.value, amount: '' }))}
                      placeholder="Custom"
                      min="1"
                      className="form-control form-control-sm"
                    />
                  </div>

                  {/* Email */}
                  <div className="mb-3">
                    <label className="form-label small fw-bold">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-control form-control-sm"
                      required
                    />
                  </div>

                  {/* Buttons */}
                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm fw-bold flex-fill"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader size={16} className="me-1" style={{ animation: 'spin 1s linear infinite' }} />
                          Processing...
                        </>
                      ) : (
                        'Donate'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="btn btn-secondary btn-sm fw-bold flex-fill"
                      disabled={loading}
                    >
                      Later
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Auto-popup Hook Component - Shows only once per session
export const DonationPopup = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Check if modal has been shown in this session
    const hasShownModal = sessionStorage.getItem('donationModalShown');

    if (!hasShownModal) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        // Mark that modal has been shown in this session
        sessionStorage.setItem('donationModalShown', 'true');
      }, 5000); // Show after 5 seconds

      return () => clearTimeout(timer);
    }
  }, []);

  return <DonationModal isOpen={isOpen} onClose={() => setIsOpen(false)} />;
};

export default DonationModal;